import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { EditCustomerModal } from '../components/domain/EditCustomerModal';
import { callBillingAPI } from '../utils/api';
import { PlusIcon, SearchIcon, EditIcon, AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
// Customer from getCustomers (limited fields)
interface ApiCustomer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization?: string;
  terms_accepted: number | string;
}
// Customer for display in table
interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization?: string;
  terms_accepted: boolean;
}
// Full customer details from getCustomer
interface FullCustomerDetails {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization: string;
  address_street: string;
  address_city: string;
  address_zip: string;
  address_state: string;
  terms_accepted: number | string;
}
// Storage key for persisting customer filters
export const CUSTOMER_FILTERS_STORAGE_KEY = 'customerFilters';
export function Customers() {
  const {
    selectedCompanyId,
    selectedCompany
  } = useData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    id: urlCustomerId
  } = useParams<{
    id?: string;
  }>();
  // Check if current company is TKDscore
  const isTKDscore = selectedCompany?.name?.toLowerCase().includes('tkdscore') || false;
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // Get filter values from URL search params
  const searchQuery = searchParams.get('search') || '';
  const pageParam = searchParams.get('page');
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCustomerDetails, setIsLoadingCustomerDetails] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organization: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    terms_accepted: false,
    wholesale_customer: false,
    websites: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Helper function to convert terms_accepted to boolean
  const convertTermsAccepted = (value: number | string | boolean): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === '1' || value === 'true';
    return false;
  };
  // Fetch customers from API
  const fetchCustomers = async (page: number = 1, search: string = '') => {
    if (!selectedCompanyId) return;
    setIsLoadingCustomers(true);
    setCustomersError(null);
    try {
      const response = await callBillingAPI('getCustomers', {
        company_id: selectedCompanyId,
        page: page,
        search: search
      });
      console.log('Customers API Response:', response);
      // Transform API customers (limited fields)
      const apiCustomers: ApiCustomer[] = response.items || [];
      const transformedCustomers: Customer[] = apiCustomers.map(apiCustomer => ({
        id: apiCustomer.id,
        first_name: apiCustomer.first_name,
        last_name: apiCustomer.last_name,
        email: apiCustomer.email,
        phone: apiCustomer.phone,
        organization: apiCustomer.organization || '',
        terms_accepted: convertTermsAccepted(apiCustomer.terms_accepted)
      }));
      console.log('Transformed customers:', transformedCustomers);
      setCustomers(transformedCustomers);
      setTotalCount(response.total_count || 0);
      setTotalPages(response.total_pages || 0);
      setHasNextPage(response.has_next_page || false);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomersError(error instanceof Error ? error.message : 'Failed to load customers');
    } finally {
      setIsLoadingCustomers(false);
    }
  };
  // Fetch full customer details
  const fetchCustomerDetails = async (customerId: string) => {
    setIsLoadingCustomerDetails(true);
    try {
      console.log('Fetching customer details for ID:', customerId);
      const customerDetails: FullCustomerDetails = await callBillingAPI('getCustomer', {
        id: customerId
      });
      console.log('Customer details response:', customerDetails);
      // Populate form with full details
      setFormData({
        first_name: customerDetails.first_name || '',
        last_name: customerDetails.last_name || '',
        email: customerDetails.email || '',
        phone: customerDetails.phone || '',
        organization: customerDetails.organization || '',
        address_street: customerDetails.address_street || '',
        address_city: customerDetails.address_city || '',
        address_state: customerDetails.address_state || '',
        address_zip: customerDetails.address_zip || '',
        terms_accepted: convertTermsAccepted(customerDetails.terms_accepted),
        wholesale_customer: !!(customerDetails as any).wholesale_customer,
        websites: (customerDetails as any).websites || ''
      });
      console.log('Form data set with terms_accepted:', convertTermsAccepted(customerDetails.terms_accepted));
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      setFormErrors({
        submit: error instanceof Error ? error.message : 'Failed to load customer details'
      });
    } finally {
      setIsLoadingCustomerDetails(false);
    }
  };
  // Save current filters to sessionStorage whenever they change
  useEffect(() => {
    const currentFilters = searchParams.toString();
    if (currentFilters) {
      sessionStorage.setItem(CUSTOMER_FILTERS_STORAGE_KEY, currentFilters);
    } else {
      sessionStorage.removeItem(CUSTOMER_FILTERS_STORAGE_KEY);
    }
  }, [searchParams]);
  // Sync search input with URL param on mount
  useEffect(() => {
    setSearchInput(searchQuery);
  }, []);
  // Helper to update URL search params
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset page when filters change (except when explicitly setting page)
    if (!('page' in updates)) {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };
  // Fetch customers when company or URL params change
  useEffect(() => {
    const page = pageParam ? parseInt(pageParam) : 1;
    fetchCustomers(page, searchQuery);
  }, [selectedCompanyId, searchQuery, pageParam]);
  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        updateSearchParams({
          search: searchInput || null
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  const handleNextPage = () => {
    if (hasNextPage) {
      updateSearchParams({
        page: String(currentPage + 1)
      });
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      updateSearchParams({
        page: currentPage > 2 ? String(currentPage - 1) : null
      });
    }
  };
  const openCreateModal = () => {
    setEditingCustomerId(null);
    setIsModalOpen(true);
  };
  const openEditModal = async (customer: Customer) => {
    setEditingCustomerId(customer.id);
    setIsModalOpen(true);
  };
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    // Required fields: email and first_name
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    // All other fields are optional
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    setFormErrors({});
    try {
      if (editingCustomerId) {
        // Edit existing customer
        console.log('Editing customer with data:', {
          id: editingCustomerId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          organization: formData.organization,
          address_street: formData.address_street,
          address_city: formData.address_city,
          address_zip: formData.address_zip,
          address_state: formData.address_state,
          terms_accepted: formData.terms_accepted ? 1 : 0,
          wholesale_customer: formData.wholesale_customer ? 1 : 0
        });
        await callBillingAPI('editCustomer', {
          id: editingCustomerId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          organization: formData.organization,
          address_street: formData.address_street,
          address_city: formData.address_city,
          address_zip: formData.address_zip,
          address_state: formData.address_state,
          terms_accepted: formData.terms_accepted ? 1 : 0,
          wholesale_customer: formData.wholesale_customer ? 1 : 0
        });
        console.log('Customer updated successfully');
      } else {
        // Add new customer
        console.log('Adding customer with data:', {
          company_id: selectedCompanyId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          organization: formData.organization,
          address_street: formData.address_street,
          address_city: formData.address_city,
          address_zip: formData.address_zip,
          address_state: formData.address_state,
          terms_accepted: formData.terms_accepted ? 1 : 0,
          wholesale_customer: formData.wholesale_customer ? 1 : 0
        });
        const response = await callBillingAPI('addCustomer', {
          company_id: selectedCompanyId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          organization: formData.organization,
          address_street: formData.address_street,
          address_city: formData.address_city,
          address_zip: formData.address_zip,
          address_state: formData.address_state,
          terms_accepted: formData.terms_accepted ? 1 : 0,
          wholesale_customer: formData.wholesale_customer ? 1 : 0
        });
        console.log('Add customer response:', response);
        console.log('New customer ID:', response.id);
      }
      setIsModalOpen(false);
      await fetchCustomers(currentPage, searchQuery);
    } catch (error) {
      console.error('Failed to save customer:', error);
      setFormErrors({
        submit: error instanceof Error ? error.message : 'Failed to save customer'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const columns = [{
    key: 'name',
    header: 'Name',
    render: (customer: Customer) => {
      const fullName = `${customer.first_name} ${customer.last_name}`.trim();
      const showOrg = customer.organization && customer.organization.trim() !== fullName;
      return <div data-id="element-982">
            <a href={`/customers/${customer.id}`} onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          navigate(`/customers/${customer.id}`);
        }} className="font-medium text-blue-600 hover:text-blue-800 hover:underline" data-id="element-983">
              {customer.first_name} {customer.last_name}
            </a>
            {showOrg && <p className="text-xs text-gray-500" data-id="element-984">{customer.organization}</p>}
          </div>;
    }
  }, {
    key: 'email',
    header: 'Email',
    render: (customer: Customer) => <a href={`mailto:${customer.email}`} className="text-blue-600 hover:text-blue-800" data-id="element-985">
          {customer.email}
        </a>
  }, {
    key: 'phone',
    header: 'Phone',
    render: (customer: Customer) => <span data-id="element-986">{customer.phone}</span>
  }, {
    key: 'terms',
    header: 'Terms',
    render: (customer: Customer) => <Badge variant={customer.terms_accepted ? 'success' : 'warning'} data-id="element-987">
          {customer.terms_accepted ? 'Accepted' : 'Pending'}
        </Badge>
  }, {
    key: 'actions',
    header: '',
    render: (customer: Customer) => <div className="flex items-center justify-end" data-id="element-988">
          <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        openEditModal(customer);
      }} aria-label="Edit customer" data-id="element-989">
            <EditIcon className="w-4 h-4" data-id="element-990" />
          </Button>
        </div>
  }];
  return <div className="space-y-6" data-id="element-991">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-992">
        <div data-id="element-993">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-994">Customers</h1>
          <p className="text-gray-500 mt-1" data-id="element-995">
            Manage your customer database ({totalCount} total)
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-997" />} onClick={openCreateModal} data-id="element-996">
          Add Customer
        </Button>
      </div>

      {/* Search */}
      <Card data-id="element-998">
        <div className="relative" data-id="element-999">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1000" />
          <Input placeholder="Search customers by name, email, or phone..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-10" data-id="element-1001" />
        </div>
      </Card>

      {/* Table */}
      {isLoadingCustomers ? <Card data-id="element-1002">
          <div className="p-12 text-center" data-id="element-1003">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1004"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-1005">Loading customers...</p>
          </div>
        </Card> : customersError ? <Card data-id="element-1006">
          <div className="p-12 text-center" data-id="element-1007">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-1008">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1009" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-1010">
              Failed to load customers
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1011">{customersError}</p>
            <Button size="sm" onClick={() => fetchCustomers(currentPage, searchQuery)} data-id="element-1012">
              Retry
            </Button>
          </div>
        </Card> : <>
          <Table columns={columns} data={customers} keyExtractor={c => c.id} emptyMessage="No customers found" data-id="element-1013" />

          {/* Pagination */}
          {totalCount > 0 && <Card data-id="element-1014">
              <div className="flex items-center justify-between" data-id="element-1015">
                <p className="text-sm text-gray-600" data-id="element-1016">
                  Page {currentPage} of {totalPages || 1}
                </p>
                <div className="flex gap-2" data-id="element-1017">
                  <Button variant="secondary" size="sm" onClick={handlePrevPage} disabled={currentPage === 1} leftIcon={<ChevronLeftIcon className="w-4 h-4" data-id="element-1019" />} data-id="element-1018">
                    Previous
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleNextPage} disabled={!hasNextPage} rightIcon={<ChevronRightIcon className="w-4 h-4" data-id="element-1021" />} data-id="element-1020">
                    Next
                  </Button>
                </div>
              </div>
            </Card>}
        </>}

      <EditCustomerModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSaved={() => fetchCustomers(currentPage, searchQuery)} customerId={editingCustomerId} data-id="element-1022" />
    </div>;
}